import { useEffect, useRef, useState } from "react";
import type { Components } from "react-markdown";
import styled from "@emotion/styled";
import { Input } from "antd5";

import { normaliseMarkdownText } from "lib/utils";
import { createUseDebounce } from "../../../lib/debounce";
import { Markdown } from "../../reports/builder_components/Markdown";

export type EditableMarkdownTextProps = {
  initialValue?: string;
  placeholder?: string;
  onSave?: (value: string) => void;
  hasEditAccess: boolean;
  padding?: string;
  autoSize?: boolean | { minRows?: number; maxRows?: number } | undefined;
  /**
   * When set, caps the rendered height of both the Markdown preview and the
   * textarea edit view to this value and scrolls overflow internally. This
   * keeps the field at a consistent height even when the content is long.
   */
  maxHeight?: number | string;
  /**
   * When set, applies a minimum height to the container in both editing and
   * preview states, so the field has a consistent height regardless of mode.
   * Pick a value that matches the rendered textarea height for the chosen
   * `autoSize.minRows` (≈ 22px per antd row + 16px container padding).
   */
  minHeight?: number | string;
  /**
   * When true, renders a border around the preview (non-editing) state. The
   * antd textarea has its own border in edit mode, so the preview border is
   * suppressed while editing to avoid a double border.
   */
  bordered?: boolean;
  /**
   * Passed through to the underlying `Markdown` component so callers can
   * override element rendering (e.g. heading sizes) in preview mode.
   */
  customComponents?: Components;
};

export default function EditableMarkdownText({
  initialValue = "",
  placeholder = "Click to edit",
  onSave,
  hasEditAccess,
  padding = "8px",
  autoSize = true,
  maxHeight,
  minHeight,
  bordered = false,
  customComponents,
}: EditableMarkdownTextProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState<string>(initialValue);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [inputWidth, setInputWidth] = useState<number>();

  const useDebounce500 = createUseDebounce(500);
  const debouncedSave = useDebounce500((textToSave: string) => {
    if (textToSave !== initialValue && textToSave.length > 0) {
      onSave?.(textToSave);
    }
  });

  // Set input width to match container width
  useEffect(() => {
    if (containerRef.current) {
      setInputWidth(containerRef.current.offsetWidth);
    }
  }, [isEditing]);

  const handleBlur = async () => {
    setIsEditing(false);
    debouncedSave(text);
  };

  const textVal = () => {
    if (text === "") {
      return placeholder;
    }
    return text;
  };

  return (
    <Container
      ref={containerRef}
      padding={padding}
      maxHeight={maxHeight}
      minHeight={minHeight}
      bordered={bordered}
      isEditing={isEditing}
      onClick={() => {
        if (hasEditAccess) {
          setIsEditing(true);
        }
      }}
    >
      {isEditing ? (
        <Input.TextArea
          style={{ width: inputWidth, padding: 0 }}
          placeholder={placeholder}
          value={text.replace(/\\n/g, "\n")}
          onBlur={handleBlur}
          onChange={(e) => {
            setText(e.target.value);
          }}
          autoSize={autoSize}
          autoFocus
          // If bordered prop is applied, we use a border on the container instead of the textarea to avoid a
          //  double border while editing
          variant={bordered ? "borderless" : undefined}
        />
      ) : (
        <Markdown
          text={normaliseMarkdownText(textVal())}
          targetType="new-tab"
          customComponents={customComponents}
        />
      )}
    </Container>
  );
}

const Container = styled.div<{
  padding: string;
  maxHeight?: number | string;
  minHeight?: number | string;
  bordered?: boolean;
  isEditing?: boolean;
}>(({ padding, maxHeight, minHeight, bordered, isEditing, theme }) => {
  // When `bordered` is opted in, mirror an antd input: focused →
  // colorPrimary (blue600 / sysPrimaryHover), idle → sysBorderDecorative.
  // The bordered path always renders a 1px border, so toggling editing only
  // changes the colour, never the box dimensions. Callers that don't opt
  // in get no border at all
  const borderStyle = bordered
    ? {
        border: `1px solid ${
          isEditing ? theme.colors.sysPrimaryHover : theme.colors.sysBorderDecorative
        }`,
      }
    : undefined;

  return {
    cursor: "pointer",
    padding,
    borderRadius: "4px",
    transition: "all 0.2s ease-in-out",
    ...(maxHeight !== undefined && {
      maxHeight,
      overflowY: "auto",
    }),
    ...(minHeight !== undefined && { minHeight }),
    ...borderStyle,

    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.02)",
    },
  };
});
